import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
} from "firebase/firestore";
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  CalendarDaysIcon,
  CurrencyDollarIcon,
  DocumentTextIcon,
  ArrowsRightLeftIcon,
  CheckBadgeIcon,
  ArrowUturnLeftIcon,
  XCircleIcon,
  ArrowPathIcon,
  UserPlusIcon,
  UserMinusIcon,
  PaperAirplaneIcon,
  BoltIcon,
} from "@heroicons/react/20/solid";
import { db } from "../firebase";
import { useAuth } from "./AuthProvider";
import { logBitacora, BITACORA_ACTIONS, getActionLabel } from "../utils/bitacora";
import { showAlert } from "../alertService";
import { Button } from "./button";
import { Alert } from "./alert";

const SYSTEM_EVENT_ICONS = {
  // Lead lifecycle
  [BITACORA_ACTIONS.LEAD_CREATE]: PlusIcon,
  [BITACORA_ACTIONS.LEAD_EDIT]: PencilSquareIcon,
  [BITACORA_ACTIONS.LEAD_STATUS_CHANGE]: ArrowsRightLeftIcon,
  [BITACORA_ACTIONS.LEAD_DELETE]: TrashIcon,
  [BITACORA_ACTIONS.LEAD_ADD_DATE]: CalendarDaysIcon,
  [BITACORA_ACTIONS.LEAD_REMOVE_DATE]: CalendarDaysIcon,
  [BITACORA_ACTIONS.LEAD_EDIT_DESGLOSE]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.LEAD_TOGGLE_IVA]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.LEAD_COTIZACION_GENERATE]: DocumentTextIcon,
  [BITACORA_ACTIONS.LEAD_CONVERT]: CheckBadgeIcon,
  [BITACORA_ACTIONS.LEAD_EDIT_NOTES]: PencilSquareIcon,
  // Event lifecycle
  [BITACORA_ACTIONS.EVENT_CREATE]: PlusIcon,
  [BITACORA_ACTIONS.EVENT_EDIT]: PencilSquareIcon,
  [BITACORA_ACTIONS.EVENT_DELETE]: TrashIcon,
  [BITACORA_ACTIONS.EVENT_CANCEL]: XCircleIcon,
  [BITACORA_ACTIONS.EVENT_REACTIVATE]: ArrowPathIcon,
  [BITACORA_ACTIONS.EVENT_EDIT_NOTES]: PencilSquareIcon,
  [BITACORA_ACTIONS.EVENT_EDIT_PRECIO]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.EVENT_EDIT_DESGLOSE]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.EVENT_TOGGLE_IVA]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.EVENT_PROVEEDOR_ASSIGN]: UserPlusIcon,
  [BITACORA_ACTIONS.EVENT_PROVEEDOR_UNASSIGN]: UserMinusIcon,
  [BITACORA_ACTIONS.EVENT_PLANNER_ASSIGN]: UserPlusIcon,
  [BITACORA_ACTIONS.EVENT_PLANNER_UNASSIGN]: UserMinusIcon,
  // Payments
  [BITACORA_ACTIONS.PAYMENT_CREATE]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.PAYMENT_DELETE]: TrashIcon,
  [BITACORA_ACTIONS.PAYMENT_EDIT_AMOUNT]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.PAYMENT_EDIT_METHOD]: CurrencyDollarIcon,
  [BITACORA_ACTIONS.PAYMENT_EDIT_NOTES]: PencilSquareIcon,
  // Refunds
  [BITACORA_ACTIONS.REFUND_CREATE]: ArrowUturnLeftIcon,
  [BITACORA_ACTIONS.REFUND_DELETE]: TrashIcon,
  [BITACORA_ACTIONS.REFUND_EDIT_AMOUNT]: ArrowUturnLeftIcon,
  [BITACORA_ACTIONS.REFUND_EDIT_METHOD]: ArrowUturnLeftIcon,
  [BITACORA_ACTIONS.REFUND_EDIT_NOTES]: PencilSquareIcon,
  // Cotizaciones
  [BITACORA_ACTIONS.COTIZACION_GENERATE]: DocumentTextIcon,
  [BITACORA_ACTIONS.COTIZACION_MARK_SENT]: PaperAirplaneIcon,
};

const ACCENT = "#1b4d2e";
const MAX_TEXTO = 2000;

const formatNoteDate = (ts) => {
  if (!ts?.toDate) return "";
  return ts.toDate().toLocaleString("es-MX", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const fmtMoney = (n) => `$${Number(n).toLocaleString("es-MX")}`;

const truncate = (str, max = 60) => {
  const s = String(str).trim();
  return s.length > max ? `${s.slice(0, max)}…` : s;
};

// Produce a compact, human-readable summary of a bitácora entry's
// `details` payload for the timeline row. Returns "" if nothing useful.
function formatSystemEventDetail(entry) {
  const d = entry.details || {};
  const parts = [];

  // Status / value transitions
  if (d.from && d.to) parts.push(`${d.from} → ${d.to}`);

  // Amount transitions (PAYMENT_EDIT_AMOUNT, REFUND_EDIT_AMOUNT)
  if (d.oldMonto != null && d.newMonto != null) {
    parts.push(`${fmtMoney(d.oldMonto)} → ${fmtMoney(d.newMonto)}`);
  }
  // Single amount (PAYMENT_CREATE, PAYMENT_DELETE, REFUND_CREATE, REFUND_DELETE)
  else if (d.monto != null) {
    parts.push(fmtMoney(d.monto));
  }

  // Event price edit (EVENT_EDIT_PRECIO)
  if (d.precio != null) parts.push(fmtMoney(d.precio));

  // Cotización total (LEAD_COTIZACION_GENERATE, COTIZACION_GENERATE)
  if (d.total != null) parts.push(`total ${fmtMoney(d.total)}`);

  // Payment method
  if (d.metodo) parts.push(d.metodo);

  // Folio (cotizaciones)
  if (d.folio) parts.push(d.folio);

  // IVA toggle
  if (d.ivaEnabled === true) parts.push("IVA activado");
  if (d.ivaEnabled === false) parts.push("IVA desactivado");

  // Interest-date label (LEAD_ADD_DATE / LEAD_REMOVE_DATE)
  if (d.label) parts.push(truncate(d.label, 50));

  // Deletes with a reason (LEAD_DELETE)
  if (d.reason) parts.push(`razón: ${truncate(d.reason, 50)}`);

  // Conversion target (LEAD_CONVERT)
  if (d.eventName) parts.push(`evento: ${truncate(d.eventName, 40)}`);

  // Proveedor assign/unassign
  if (d.proveedorNombre) {
    parts.push(
      d.categoria ? `${d.proveedorNombre} (${d.categoria})` : d.proveedorNombre
    );
  }

  // Planner assign/unassign
  if (d.plannerName || d.plannerNombre) {
    parts.push(d.plannerName || d.plannerNombre);
  }

  // Multi-field edits (LEAD_EDIT, EVENT_EDIT)
  if (Array.isArray(d.changes) && d.changes.length > 0) {
    const c = d.changes[0];
    const fragment = `${c.field}: ${truncate(c.from, 24)} → ${truncate(c.to, 24)}`;
    parts.push(
      d.changes.length === 1
        ? fragment
        : `${fragment} (+${d.changes.length - 1} más)`
    );
  }

  return parts.length > 0 ? parts.join(" · ") : "";
}

const LeadNotasTimeline = ({
  leadId,
  venueId,
  leadTopic = "",
  isViewer = false,
  readOnly = false,
  // Optional: array of { entity, entityId } filter pairs to merge into the
  // system-event feed. Defaults to [{ entity: 'lead', entityId: leadId }] for
  // the lead-page use case. The event-side Seguimientos tab passes a broader
  // set so payments/refunds/cotizaciones/event edits all show up.
  systemEventFilters = null,
  // Optional: array of path segments to the notas subcollection. Defaults to
  // ["venueContactForms", leadId, "notas"] when leadId is set. EventPage
  // passes the event's own notas path for manually-created events.
  notasPath = null,
  // Optional: override bitácora entity label on nota mutation logs.
  // Defaults to "lead_nota". EventPage passes "event_nota" so admin audit
  // traces distinguish lead-thread vs event-thread notas.
  notaBitacoraEntity = "lead_nota",
  // Optional: BITACORA_ACTIONS values to use for create/edit/delete logs.
  // Defaults to LEAD_NOTA_*. EventPage passes EVENT_NOTA_* so the action
  // label is "Nota de evento" instead of "Nota de lead".
  notaActions = null,
  // Optional: extra fields merged into bitácora `details` on each nota
  // mutation. Defaults to { leadId, leadTopic }. EventPage passes
  // { eventId, eventName } for event-based notas.
  notaParentDetails = null,
}) => {
  const { user, userInfo } = useAuth();

  const [notas, setNotas] = useState([]);
  const [systemEventsByKey, setSystemEventsByKey] = useState({});
  const [loading, setLoading] = useState(true);
  const [nuevoTexto, setNuevoTexto] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editDraft, setEditDraft] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState(null);

  const composerDisabled = readOnly || isViewer;

  // Resolve effective notas subcollection path.
  const effectiveNotasPath = useMemo(() => {
    if (notasPath && notasPath.length > 0) return notasPath;
    if (leadId) return ["venueContactForms", leadId, "notas"];
    return null;
  }, [notasPath, leadId]);
  const notasPathKey = effectiveNotasPath ? effectiveNotasPath.join("/") : "";

  // Defaults for bitácora details when the parent didn't override.
  const effectiveNotaDetails = notaParentDetails || { leadId, leadTopic };
  const effectiveNotaActions = notaActions || {
    create: BITACORA_ACTIONS.LEAD_NOTA_CREATE,
    edit: BITACORA_ACTIONS.LEAD_NOTA_EDIT,
    delete: BITACORA_ACTIONS.LEAD_NOTA_DELETE,
  };

  useEffect(() => {
    if (!effectiveNotasPath) {
      setNotas([]);
      setLoading(false);
      return;
    }
    const q = query(
      collection(db, ...effectiveNotasPath),
      orderBy("createdAt", "desc")
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setNotas(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Notas snapshot error:", err);
        setLoading(false);
      }
    );
    return unsub;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notasPathKey]);

  // Resolve effective system-event filters: default to lead-scoped if not
  // passed explicitly. Memoized + stringified so dependency comparison is stable.
  const effectiveFilters = useMemo(() => {
    if (systemEventFilters && systemEventFilters.length > 0) {
      return systemEventFilters.filter((f) => f.entity && f.entityId);
    }
    return leadId ? [{ entity: "lead", entityId: leadId }] : [];
  }, [systemEventFilters, leadId]);
  const filtersKey = effectiveFilters
    .map((f) => `${f.entity}:${f.entityId}`)
    .join("|");

  // System events derived from bitácora — one subscription per (entity, entityId)
  // pair. Stored keyed so each stream replaces its own slice. Permission-denied
  // is non-fatal (degrades to notas only).
  useEffect(() => {
    if (!venueId || effectiveFilters.length === 0) {
      setSystemEventsByKey({});
      return;
    }
    const unsubs = effectiveFilters.map((f) => {
      const key = `${f.entity}:${f.entityId}`;
      const q = query(
        collection(db, "venues", venueId, "bitacora"),
        where("entity", "==", f.entity),
        where("entityId", "==", f.entityId),
        orderBy("timestamp", "desc")
      );
      return onSnapshot(
        q,
        (snap) => {
          setSystemEventsByKey((prev) => ({
            ...prev,
            [key]: snap.docs.map((d) => ({ id: d.id, ...d.data() })),
          }));
        },
        (err) => {
          if (err?.code !== "permission-denied") {
            console.warn("Bitácora snapshot error:", err);
          }
          setSystemEventsByKey((prev) => ({ ...prev, [key]: [] }));
        }
      );
    });
    return () => unsubs.forEach((u) => u && u());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId, filtersKey]);

  const timelineItems = useMemo(() => {
    const notaItems = notas.map((n) => ({
      kind: "nota",
      id: `nota-${n.id}`,
      raw: n,
      ts: n.createdAt?.toMillis ? n.createdAt.toMillis() : 0,
    }));
    const sysItems = Object.values(systemEventsByKey)
      .flat()
      .map((s) => ({
        kind: "system",
        id: `sys-${s.id}`,
        raw: s,
        ts: s.timestamp?.toMillis ? s.timestamp.toMillis() : 0,
      }));
    return [...notaItems, ...sysItems].sort((a, b) => b.ts - a.ts);
  }, [notas, systemEventsByKey]);

  const handleAdd = async () => {
    const texto = nuevoTexto.trim();
    if (!texto || submitting || composerDisabled || !effectiveNotasPath) return;
    setSubmitting(true);
    try {
      const ref = collection(db, ...effectiveNotasPath);
      const docRef = await addDoc(ref, {
        texto,
        authorId: user?.uid || null,
        authorName: userInfo?.name || "",
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      logBitacora(
        venueId,
        {
          action: effectiveNotaActions.create,
          entity: notaBitacoraEntity,
          entityId: docRef.id,
          entityName: leadTopic || "",
          details: effectiveNotaDetails,
        },
        userInfo,
        user
      );
      setNuevoTexto("");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveEdit = async (id) => {
    const texto = editDraft.trim();
    if (!texto || !effectiveNotasPath) {
      setEditingId(null);
      return;
    }
    try {
      await updateDoc(doc(db, ...effectiveNotasPath, id), {
        texto,
        updatedAt: serverTimestamp(),
      });
      logBitacora(
        venueId,
        {
          action: effectiveNotaActions.edit,
          entity: notaBitacoraEntity,
          entityId: id,
          entityName: leadTopic || "",
          details: effectiveNotaDetails,
        },
        userInfo,
        user
      );
      setEditingId(null);
      setEditDraft("");
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  const handleDelete = async () => {
    if (!deleteCandidate || !effectiveNotasPath) return;
    try {
      await deleteDoc(doc(db, ...effectiveNotasPath, deleteCandidate.id));
      logBitacora(
        venueId,
        {
          action: effectiveNotaActions.delete,
          entity: notaBitacoraEntity,
          entityId: deleteCandidate.id,
          entityName: leadTopic || "",
          details: effectiveNotaDetails,
        },
        userInfo,
        user
      );
      setDeleteCandidate(null);
    } catch (e) {
      console.error(e);
      showAlert("Error: " + e.message, "error");
    }
  };

  return (
    <div className="space-y-4">
      {/* Composer */}
      {!composerDisabled && (
        <div className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-900">
          <textarea
            value={nuevoTexto}
            onChange={(e) => setNuevoTexto(e.target.value.slice(0, MAX_TEXTO))}
            placeholder="Agrega un seguimiento... (resumen de llamada, próximo paso, etc.)"
            rows={3}
            maxLength={MAX_TEXTO}
            className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
          />
          <div className="mt-2 flex items-center justify-between">
            <span
              className={`text-xs ${
                nuevoTexto.length >= MAX_TEXTO - 200
                  ? "text-amber-600"
                  : "text-zinc-400"
              }`}
            >
              {nuevoTexto.length >= MAX_TEXTO - 200
                ? `${nuevoTexto.length} / ${MAX_TEXTO}`
                : ""}
            </span>
            <button
              type="button"
              onClick={handleAdd}
              disabled={!nuevoTexto.trim() || submitting}
              className="px-4 py-2 rounded-lg text-sm font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              style={{ backgroundColor: ACCENT }}
            >
              <PlusIcon className="h-4 w-4" />
              {submitting ? "Agregando..." : "Agregar nota"}
            </button>
          </div>
        </div>
      )}

      {/* Feed */}
      {loading ? (
        <div className="py-8 text-center text-sm text-zinc-400">Cargando...</div>
      ) : timelineItems.length === 0 ? (
        <div className="py-8 text-center text-sm text-zinc-400 italic">
          Aún no hay actividad.
        </div>
      ) : (
        <div className="space-y-2">
          {timelineItems.map((item) => {
            if (item.kind === "nota") {
              const n = item.raw;
              const isEditing = editingId === n.id;
              const wasEdited =
                n.updatedAt?.toMillis &&
                n.createdAt?.toMillis &&
                n.updatedAt.toMillis() - n.createdAt.toMillis() > 1000;
              return (
                <div
                  key={item.id}
                  className="rounded-xl border border-zinc-200 bg-white p-4 shadow-sm dark:border-zinc-700/60 dark:bg-zinc-900"
                >
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        autoFocus
                        value={editDraft}
                        onChange={(e) =>
                          setEditDraft(e.target.value.slice(0, MAX_TEXTO))
                        }
                        rows={3}
                        maxLength={MAX_TEXTO}
                        className="w-full rounded-lg border border-zinc-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1b4d2e] dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100"
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingId(null);
                            setEditDraft("");
                          }}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                        >
                          Cancelar
                        </button>
                        <button
                          type="button"
                          onClick={() => handleSaveEdit(n.id)}
                          className="px-3 py-1.5 rounded-lg text-sm font-medium text-white"
                          style={{ backgroundColor: ACCENT }}
                        >
                          Guardar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="mb-1.5 text-xs">
                          <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                            {n.authorName || "Anónimo"}
                          </span>
                          <span className="text-zinc-400">
                            {" · "}
                            {formatNoteDate(n.createdAt)}
                            {wasEdited && ` · editada ${formatNoteDate(n.updatedAt)}`}
                          </span>
                        </div>
                        <div className="text-sm text-zinc-800 whitespace-pre-wrap dark:text-zinc-200">
                          {n.texto}
                        </div>
                      </div>
                      {!composerDisabled && (
                        <div className="flex gap-1">
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(n.id);
                              setEditDraft(n.texto || "");
                            }}
                            className="text-zinc-400 hover:text-zinc-600 p-1 dark:hover:text-zinc-200"
                            title="Editar"
                          >
                            <PencilSquareIcon className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => setDeleteCandidate(n)}
                            className="text-zinc-400 hover:text-red-500 p-1"
                            title="Eliminar"
                          >
                            <TrashIcon className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            }
            // System event row
            const s = item.raw;
            const Icon = SYSTEM_EVENT_ICONS[s.action] || BoltIcon;
            const detailHint = formatSystemEventDetail(s);
            return (
              <div
                key={item.id}
                className="flex items-start gap-3 rounded-lg border border-zinc-100 dark:border-zinc-700/40 bg-zinc-50/70 dark:bg-zinc-800/30 px-3 py-2"
              >
                <div
                  className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md mt-0.5"
                  style={{ backgroundColor: ACCENT + "1a" }}
                >
                  <Icon className="h-3.5 w-3.5" style={{ color: ACCENT }} />
                </div>
                <div className="flex-1 min-w-0 text-xs">
                  <div>
                    <span className="font-semibold text-zinc-700 dark:text-zinc-200">
                      {s.userName || "Sistema"}
                    </span>
                    <span className="text-zinc-600 dark:text-zinc-300">
                      {" · "}
                      {getActionLabel(s.action)}
                    </span>
                  </div>
                  {detailHint && (
                    <div className="mt-0.5 text-zinc-600 dark:text-zinc-400">
                      {detailHint}
                    </div>
                  )}
                  <div className="mt-0.5 text-zinc-400">
                    {formatNoteDate(s.timestamp)}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Alert open={!!deleteCandidate} onClose={() => setDeleteCandidate(null)}>
        <div className="font-semibold mb-2">¿Eliminar esta nota?</div>
        <div className="mb-4 text-sm text-zinc-600 dark:text-zinc-400">
          Esta acción no se puede deshacer.
        </div>
        <div className="flex gap-2 justify-end">
          <Button outline onClick={() => setDeleteCandidate(null)}>
            Cancelar
          </Button>
          <Button color="red" onClick={handleDelete}>
            Eliminar
          </Button>
        </div>
      </Alert>
    </div>
  );
};

export default LeadNotasTimeline;
